"use client";

import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";

import { useShell } from "@/components/shell/shell-context";
import { ApiError, api, loginUrl } from "@/lib/api/client";
import type { Connection, OAuthStart, Project, ProjectUpdate } from "@/lib/api/types";
import { formatCount } from "@/lib/format";
import { navigateTo } from "@/lib/navigation";

import { ProjectDefaults } from "./project-defaults";
import { SourceCard } from "./source-card";

const PATH = "/settings/connections";

const OAUTH_ERRORS: Record<string, string> = {
  denied: "Der Zugriff wurde bei Google nicht erlaubt.",
  invalid_state: "Der Anmeldevorgang ist ungültig oder abgelaufen. Bitte erneut starten.",
  exchange_failed: "Google hat den Zugriff nicht bestätigt. Bitte erneut verbinden.",
  invalid: "Die Antwort von Google war unvollständig. Bitte erneut verbinden.",
};

type Banner = { kind: "ok" | "error"; text: string } | null;

/** Liest das Ergebnis des Google-Rücksprungs aus der Adresse (nur lesend, damit es wiederholbar ist). */
function parseOAuthResult(): Banner {
  const params = new URLSearchParams(window.location.search);
  const result = params.get("oauth");
  if (!result) return null;
  if (result === "success") {
    return { kind: "ok", text: "Google-Konto verbunden. Bitte Property wählen und testen." };
  }
  const reason = OAUTH_ERRORS[params.get("reason") ?? ""];
  return { kind: "error", text: reason ?? "Die Autorisierung ist fehlgeschlagen." };
}

export function ConnectionsPage() {
  const { project } = useShell();
  // Bei Projektwechsel neu aufbauen, damit Eingaben und Meldungen nicht ins andere Projekt wandern
  return project ? <ConnectionsView key={project.id} project={project} /> : null;
}

function ConnectionsView({ project }: { project: Project }) {
  const router = useRouter();
  const { connections, connectionsFailed, setConnection, updateProject, refreshConnections } =
    useShell();
  const [revisions, setRevisions] = useState<Record<string, number>>({});
  const [testing, setTesting] = useState<Set<string>>(new Set());
  const [notices, setNotices] = useState<Record<string, string | null>>({});
  const [banner] = useState<Banner>(parseOAuthResult);
  const [saving, setSaving] = useState(false);
  const [defaultsMessage, setDefaultsMessage] = useState<Banner>(null);

  useEffect(() => {
    // Parameter aus der Adresse entfernen, damit ein Neuladen die Meldung nicht wiederholt
    if (banner) window.history.replaceState(null, "", PATH);
  }, [banner]);

  /** Bei 401 zur Anmeldung, sonst Meldung zurückgeben. */
  const fail = useCallback(
    (error: unknown): string | null => {
      if (error instanceof ApiError && error.status === 401) {
        router.replace(loginUrl(PATH));
        return null;
      }
      return error instanceof Error ? error.message : "Unbekannter Fehler";
    },
    [router],
  );

  const replace = (next: Connection) => {
    setConnection(next); // Sidebar und Seite zeigen sofort den neuen Stand
    // Neuer Stand vom Server: Karte mit frischen Eingabefeldern neu aufbauen
    setRevisions((current) => ({ ...current, [next.source]: (current[next.source] ?? 0) + 1 }));
  };

  const setBusy = (source: string, busy: boolean) =>
    setTesting((current) => {
      const next = new Set(current);
      if (busy) next.add(source);
      else next.delete(source);
      return next;
    });

  const setNotice = (source: string, text: string | null) =>
    setNotices((current) => ({ ...current, [source]: text }));

  const test = async (source: string) => {
    replace(
      await api<Connection>(`/api/connections/${source}/test?project_id=${project.id}`, {
        method: "POST",
      }),
    );
  };

  const saveAndTest = async (source: string, values: Record<string, string>) => {
    setBusy(source, true);
    setNotice(source, null);
    try {
      await api<Connection>(`/api/connections/${source}?project_id=${project.id}`, {
        method: "PUT",
        json: { values },
      });
      await test(source);
    } catch (error) {
      setNotice(source, fail(error));
    } finally {
      setBusy(source, false);
    }
  };

  const testAll = async () => {
    const configured = (connections ?? []).filter((c) => c.status !== "not_connected");
    await Promise.all(
      configured.map(async (c) => {
        setBusy(c.source, true);
        setNotice(c.source, null);
        try {
          await test(c.source);
        } catch (error) {
          setNotice(c.source, fail(error));
        } finally {
          setBusy(c.source, false);
        }
      }),
    );
  };

  const disconnect = async (source: string) => {
    setNotice(source, null);
    try {
      replace(
        await api<Connection>(`/api/connections/${source}?project_id=${project.id}`, {
          method: "DELETE",
        }),
      );
    } catch (error) {
      setNotice(source, fail(error));
    }
  };

  const authorize = async (source: string) => {
    setNotice(source, null);
    try {
      const { authorization_url } = await api<OAuthStart>(
        `/api/connections/${source}/oauth/start?project_id=${project.id}`,
        { method: "POST" },
      );
      navigateTo(authorization_url);
    } catch (error) {
      setNotice(source, fail(error));
    }
  };

  const saveDefaults = async (update: ProjectUpdate) => {
    setSaving(true);
    setDefaultsMessage(null);
    try {
      updateProject(await api<Project>(`/api/projects/${project.id}`, { method: "PATCH", json: update }));
      setDefaultsMessage({ kind: "ok", text: "Gespeichert" });
    } catch (error) {
      const text = fail(error);
      if (text) setDefaultsMessage({ kind: "error", text });
    } finally {
      setSaving(false);
    }
  };

  const list = connections ?? [];
  const connectedCount = list.filter((c) => c.status === "connected").length;
  const anyConfigured = list.some((c) => c.status !== "not_connected");

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6 px-6 py-7 sm:px-9">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="text-[13px] text-muted">Einstellungen · Projekt {project.domain}</div>
          <h1 className="text-3xl font-semibold tracking-tight">Verbindungen</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {connections && (
            <span className="text-[13px] text-muted">
              {formatCount(connectedCount)} von {formatCount(list.length)} verbunden
            </span>
          )}
          <button
            type="button"
            onClick={() => void testAll()}
            disabled={!anyConfigured || testing.size > 0}
            className="h-11 rounded-md bg-accent px-4 text-sm font-medium text-white disabled:opacity-60"
          >
            Alle testen
          </button>
        </div>
      </header>

      <div
        role="note"
        className="flex gap-3 rounded-lg border border-[#ccdaec] bg-[#eef3fa] px-[18px] py-3.5 text-sm leading-normal text-[#1e3557]"
      >
        <span>
          Zugangsdaten werden verschlüsselt im Backend gespeichert und nie an den Browser zurückgegeben.
          Verbindungstests laufen serverseitig, damit keine Keys im Frontend landen und keine
          CORS-Probleme entstehen.
        </span>
      </div>

      {banner && (
        <div
          role={banner.kind === "error" ? "alert" : "status"}
          className={`rounded-lg px-[18px] py-3 text-sm ${
            banner.kind === "error" ? "bg-danger-bg text-danger" : "bg-success-bg text-success"
          }`}
        >
          {banner.text}
        </div>
      )}

      {connections === null && !connectionsFailed && (
        <p className="text-sm text-muted">Verbindungen werden geladen …</p>
      )}

      {connections === null && connectionsFailed && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-lg bg-danger-bg px-[18px] py-3.5 text-sm text-danger"
        >
          <span>Die Verbindungen konnten nicht geladen werden.</span>
          <button
            type="button"
            onClick={refreshConnections}
            className="h-11 rounded-md border border-danger bg-white px-3.5 text-sm text-danger"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      {connections && (
        <>
          <section
            aria-label="Datenquellen"
            className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,420px),1fr))] items-start gap-5"
          >
            {connections.map((connection) => (
              <SourceCard
                key={`${connection.source}-${revisions[connection.source] ?? 0}`}
                connection={connection}
                testing={testing.has(connection.source)}
                notice={notices[connection.source] ?? null}
                onSaveAndTest={(values) => void saveAndTest(connection.source, values)}
                onDisconnect={() => void disconnect(connection.source)}
                onAuthorize={() => void authorize(connection.source)}
              />
            ))}
          </section>
          <ProjectDefaults
            key={`${project.id}-${project.country}-${project.language}-${project.cost_limit_per_job_eur}-${project.monthly_budget_eur}`}
            project={project}
            saving={saving}
            message={defaultsMessage}
            onSave={(update) => void saveDefaults(update)}
          />
        </>
      )}
    </div>
  );
}
